import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { Home, CheckSquare, Plus, Bell, FileText } from 'lucide-react';

// Page Imports
import HomePage from './pages/HomePage';
import EditorPage from './pages/EditorPage';
import NotesPage from './pages/NotesPage';
import TasksPage from './pages/TasksPage';
import RemindersPage from './pages/RemindersPage';

import './App.css';

function App() {
  // Global State for Notes and Tasks
  const [notes, setNotes] = useState([]);
  const [tasks, setTasks] = useState([]);

  // Add this inside your App component in App.js
useEffect(() => {
  const alarmInterval = setInterval(() => {
    const now = new Date();
    const currentTime = now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    const currentDate = now.toISOString().split('T')[0];

    tasks.forEach(task => {
      // Trigger if Date and Time match exactly and task isn't completed
      if (task.date === currentDate && task.time === currentTime && !task.completed && !task.alarmFired) {
        triggerAlarm(task);
      }
    });
  }, 10000); // Checks every 10 seconds

  return () => clearInterval(alarmInterval);
}, [tasks]);

const triggerAlarm = (task) => {
  // 1. Browser Notification
  if (Notification.permission === "granted") {
    new Notification(`NaSquare Alarm: ${task.text}`, { body: "Your scheduled reminder is starting now!" });
  }
  
  // 2. Visual Alert (You can use a state to show a global modal)
  alert(`⏰ ALARM: ${task.text}\n\nTime to get to work!`);
  
  // Mark as fired so it doesn't repeat every 10 seconds for that same minute
  setTasks(prev => prev.map(t => t.id === task.id ? { ...t, alarmFired: true } : t));
};

// Request permission on mount
useEffect(() => {
  if (Notification.permission !== "granted") {
    Notification.requestPermission();
  }
}, []);

  // --- Load Data from LocalStorage on Startup ---
  useEffect(() => {
    const savedNotes = JSON.parse(localStorage.getItem('ns-notes') || '[]');
    const savedTasks = JSON.parse(localStorage.getItem('ns-tasks') || '[]');
    setNotes(savedNotes);
    setTasks(savedTasks);
  }, []);

  // --- Save Data to LocalStorage whenever state changes ---
  useEffect(() => {
    localStorage.setItem('ns-notes', JSON.stringify(notes));
    localStorage.setItem('ns-tasks', JSON.stringify(tasks));
  }, [notes, tasks]);

  // Handler for saving a new note from the Editor
  const handleSaveNote = (content) => {
    const newNote = {
      id: Date.now(),
      text: content,
      date: new Date().toLocaleDateString(),
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
    setNotes([newNote, ...notes]);
  };

  return (
    <Router>
      <div className="app-shell">
        <main className="main-view">
          <Routes>
            <Route path="/" element={<HomePage tasks={tasks} notes={notes} />} />
            
            {/* Editor receives the save handler */}
            <Route path="/editor" element={<EditorPage onSave={handleSaveNote} />} />
            
            {/* Notes receives notes and the setter for deletion */}
            <Route path="/notes" element={<NotesPage notes={notes} setNotes={setNotes} />} />
            
            {/* Tasks receives the tasks and the setter */}
            <Route path="/tasks" element={<TasksPage tasks={tasks} setTasks={setTasks} />} />
            
            {/* IMPORTANT: Reminders now receives the shared tasks state */}
            <Route path="/reminders" element={<RemindersPage tasks={tasks} setTasks={setTasks} />} />
          </Routes>
        </main>
        
        {/* Navigation remains fixed at the bottom (Mobile) or side (Desktop) */}
        <NavigationBar />
      </div>
    </Router>
  );
}

// Navigation Component with Active Styling
const NavigationBar = () => {
  const location = useLocation();
  const active = (path) => location.pathname === path ? 'nav-item active' : 'nav-item';

  return (
    <nav className="nav-bar">
      <Link to="/" className={active('/')}>
        <Home size={22} />
        <span>Home</span>
      </Link>
      
      <Link to="/tasks" className={active('/tasks')}>
        <CheckSquare size={22} />
        <span>Tasks</span>
      </Link>
      
      {/* Central Plus Button (FAB) */}
      <Link to="/editor" className="fab-container">
        <div className="fab-circle">
          <Plus size={32} color="white" />
        </div>
      </Link>

      <Link to="/reminders" className={active('/reminders')}>
        <Bell size={22} />
        <span>Alerts</span>
      </Link>
      
      <Link to="/notes" className={active('/notes')}>
        <FileText size={22} />
        <span>Notes</span>
      </Link>
    </nav>
  );
};

export default App;